import { useState, useRef } from "react";
import { supabase, logActivity } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import * as XLSX from "xlsx";
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertCircle,
  Download, X, Loader2,
} from "lucide-react";

type UploadType = "student" | "volunteer";

interface Props {
  type: UploadType;
  schoolId: string;
  onComplete?: () => void;
}

interface ParsedRow {
  rowIndex: number;
  data: Record<string, string>;
  errors: string[];
}

export default function ExcelBulkUpload({ type, schoolId, onComplete }: Props) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [results, setResults] = useState<{ success: number; failed: number; errors: string[] } | null>(null);
  const [showPanel, setShowPanel] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const entityLabel = type === "student" ? t("bulk_entity_student") : t("bulk_entity_volunteer");

  const studentColumns = [
    { key: "full_name", label: t("bulk_col_full_name"), required: true },
    { key: "date_of_birth", label: t("bulk_col_dob"), required: false, hint: "YYYY-MM-DD" },
    { key: "gender", label: t("bulk_col_gender"), required: false, hint: "female or male" },
    { key: "level", label: t("bulk_col_level"), required: false, hint: "preschool, primary, or secondary" },
    { key: "guardian_name", label: t("bulk_col_guardian_name"), required: false },
    { key: "guardian_phone", label: t("bulk_col_guardian_phone"), required: false },
    { key: "guardian_email", label: t("bulk_col_guardian_email"), required: false },
    { key: "shift", label: t("bulk_col_shift"), required: false, hint: "morning or afternoon" },
  ];

  const volunteerColumns = [
    { key: "full_name", label: t("bulk_col_full_name"), required: true },
    { key: "department", label: t("bulk_col_department"), required: false, hint: "e.g. Administration, Teachers (Primary)" },
    { key: "shift", label: t("bulk_col_shift"), required: false, hint: "morning or afternoon" },
    { key: "category", label: t("bulk_col_category"), required: false, hint: "academic or housekeeping" },
    { key: "housekeeping_dept", label: t("bulk_col_housekeeping_dept"), required: false, hint: "e.g. Sanitation, Maintenance" },
  ];

  const columns = type === "student" ? studentColumns : volunteerColumns;

  const normalizeKey = (k: string) =>
    k.toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z_]/g, "");

  const validateRow = (row: Record<string, string>, rowIndex: number): ParsedRow => {
    const errors: string[] = [];
    for (const col of columns) {
      const val = row[col.key];
      if (col.required && (!val || !val.trim())) {
        errors.push(`${col.label} ${t("bulk_is_required")}`);
      }
      if (val) {
        if (col.key === "gender" && val && !["female", "male"].includes(val.toLowerCase().trim())) {
          errors.push(`${t("bulk_gender_err")} (got: ${val})`);
        }
        if (col.key === "level" && val && !["preschool", "primary", "secondary"].includes(val.toLowerCase().trim())) {
          errors.push(`${t("bulk_level_err")} (got: ${val})`);
        }
        if (col.key === "shift" && val && !["morning", "afternoon"].includes(val.toLowerCase().trim())) {
          errors.push(`${t("bulk_shift_err")} (got: ${val})`);
        }
        if (col.key === "category" && val && !["academic", "housekeeping"].includes(val.toLowerCase().trim())) {
          errors.push(`${t("bulk_category_err")} (got: ${val})`);
        }
      }
    }
    return { rowIndex, data: row, errors };
  };

  const parseFile = (file: File) => {
    setParsing(true);
    setResults(null);
    setParsedRows([]);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });

        if (jsonRows.length === 0) {
          setParsing(false);
          setResults({ success: 0, failed: 0, errors: [t("bulk_empty_file")] });
          return;
        }

        const normalized = jsonRows.map((rawRow) => {
          const normalizedRow: Record<string, string> = {};
          for (const [k, v] of Object.entries(rawRow)) {
            const nk = normalizeKey(k);
            normalizedRow[nk] = String(v).trim();
          }
          return normalizedRow;
        });

        const validated = normalized.map((row, i) => validateRow(row, i + 2));
        setParsedRows(validated);
        setParsing(false);
      } catch {
        setParsing(false);
        setResults({ success: 0, failed: 0, errors: [t("bulk_read_error")] });
      }
    };
    reader.onerror = () => {
      setParsing(false);
      setResults({ success: 0, failed: 0, errors: [t("bulk_file_error")] });
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) parseFile(file);
  };

  const downloadTemplate = () => {
    const headers = columns.map((c) => c.label);
    const sampleRow = columns.map((c) => {
      if (c.key === "full_name") return "John Doe";
      if (c.key === "gender") return "female";
      if (c.key === "level") return "primary";
      if (c.key === "shift") return "morning";
      if (c.key === "date_of_birth") return "2015-01-15";
      if (c.key === "guardian_name") return "Jane Doe";
      if (c.key === "guardian_phone") return "+265999999999";
      if (c.key === "guardian_email") return "guardian@example.com";
      if (c.key === "department") return "Teachers (Primary)";
      if (c.key === "category") return "academic";
      if (c.key === "housekeeping_dept") return "Sanitation";
      return "";
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${entityLabel}s`);
    XLSX.writeFile(wb, `${type}_upload_template.xlsx`);
  };

  const uploadData = async () => {
    if (!schoolId) {
      setResults({ success: 0, failed: 0, errors: [t("bulk_select_school_warn")] });
      return;
    }

    setUploading(true);
    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    const validRows = parsedRows.filter((r) => r.errors.length === 0);

    if (type === "student") {
      const inserts = validRows.map((r) => ({
        full_name: r.data.full_name,
        date_of_birth: r.data.date_of_birth || null,
        gender: (r.data.gender?.toLowerCase() || null) as "female" | "male" | null,
        level: (r.data.level?.toLowerCase() || null) as "preschool" | "primary" | "secondary" | null,
        guardian_name: r.data.guardian_name || null,
        guardian_phone: r.data.guardian_phone || null,
        guardian_email: r.data.guardian_email || null,
        guardian_id: user?.id || null,
        shift: (r.data.shift?.toLowerCase() as "morning" | "afternoon") || null,
        status: "approved" as const,
        school_id: schoolId,
      }));

      if (inserts.length > 0) {
        const { data: inserted, error } = await supabase.from("students").insert(inserts).select("id, full_name");
        if (error) {
          failed = inserts.length;
          errors.push(`${t("bulk_db_error")} ${error.message}`);
        } else {
          success = inserted?.length || 0;
          for (const s of inserted || []) {
            await logActivity("bulk registered student", "student", s.id, s.full_name, schoolId, "Excel bulk upload");
          }
        }
      }
      failed = parsedRows.filter((r) => r.errors.length > 0).length;
      for (const r of parsedRows.filter((r) => r.errors.length > 0)) {
        errors.push(`${t("bulk_row")} ${r.rowIndex}: ${r.errors.join(", ")}`);
      }
    } else {
      const inserts = validRows.map((r) => ({
        full_name: r.data.full_name,
        department: r.data.department || null,
        shift: (r.data.shift?.toLowerCase() as "morning" | "afternoon") || null,
        category: (r.data.category?.toLowerCase() as "academic" | "housekeeping") || null,
        housekeeping_dept: r.data.housekeeping_dept || null,
        school_id: schoolId,
      }));

      if (inserts.length > 0) {
        const { data: inserted, error } = await supabase.from("volunteers").insert(inserts).select("id, full_name");
        if (error) {
          failed = inserts.length;
          errors.push(`${t("bulk_db_error")} ${error.message}`);
        } else {
          success = inserted?.length || 0;
          for (const v of inserted || []) {
            await logActivity("bulk registered volunteer", "volunteer", v.id, v.full_name, schoolId, "Excel bulk upload");
          }
        }
      }
      failed = parsedRows.filter((r) => r.errors.length > 0).length;
      for (const r of parsedRows.filter((r) => r.errors.length > 0)) {
        errors.push(`${t("bulk_row")} ${r.rowIndex}: ${r.errors.join(", ")}`);
      }
    }

    setResults({ success, failed, errors });
    setUploading(false);

    if (success > 0 && onComplete) onComplete();
  };

  const reset = () => {
    setParsedRows([]);
    setResults(null);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validCount = parsedRows.filter((r) => r.errors.length === 0).length;
  const errorCount = parsedRows.filter((r) => r.errors.length > 0).length;

  if (!showPanel) {
    return (
      <button
        className="btn-success"
        onClick={() => setShowPanel(true)}
      >
        <Upload className="w-5 h-5" /> {t("bulk_upload_btn")} {entityLabel}s (Excel)
      </button>
    );
  }

  return (
    <div className="card animate-fadeIn">
      <div className="flex items-center justify-between mb-5 pb-3 border-b-2 border-gray-200">
        <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
          <FileSpreadsheet className="w-6 h-6" /> {t("bulk_upload_title")} {entityLabel}s
        </h2>
        <button
          onClick={() => { setShowPanel(false); reset(); }}
          className="text-gray-400 hover:text-[#D32F2F] transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <p className="text-gray-500 text-sm mb-5">
        {t("bulk_upload_desc")}
      </p>

      <div className="bg-[#F1F5F9] p-4 rounded-lg border border-[#CBD5E1] mb-5">
        <h3 className="text-[#1C3988] font-bold mb-2 flex items-center gap-2">
          <Download className="w-4 h-4" /> {t("bulk_download_template")}
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          {t("bulk_template_desc")}
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {columns.map((c) => (
            <span key={c.key} className="text-xs bg-white border border-[#CBD5E1] px-2 py-1 rounded-md">
              <strong className="text-[#1C3988]">{c.label}</strong>
              {c.required && <span className="text-[#D32F2F]"> *</span>}
              {c.hint && <span className="text-gray-400"> ({c.hint})</span>}
            </span>
          ))}
        </div>
        <button className="btn-cyan btn-sm" onClick={downloadTemplate}>
          <Download className="w-4 h-4" /> {t("bulk_download_btn")}
        </button>
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all mb-5 ${
          dragOver ? "border-[#38B6FF] bg-[#38B6FF]/5" : "border-[#CBD5E1] bg-[#F8FAFC]"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileSelect}
          className="hidden"
        />
        {parsing ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-[#38B6FF] animate-spin" />
            <p className="text-[#1C3988] font-semibold">{t("bulk_parsing")} {fileName}...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <FileSpreadsheet className="w-12 h-12 text-[#38B6FF]" />
            <p className="text-[#1C3988] font-semibold text-lg">
              {fileName ? `${t("bulk_selected")} ${fileName}` : t("bulk_drag_drop")}
            </p>
            <p className="text-gray-400 text-sm">
              {fileName ? t("bulk_or_choose") : t("bulk_or_browse")}
            </p>
            <button
              className="btn-cyan"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-5 h-5" /> {t("bulk_choose_file")}
            </button>
          </div>
        )}
      </div>

      {parsedRows.length > 0 && !results && (
        <div className="mb-5">
          <div className="flex items-center gap-4 mb-3 flex-wrap">
            <h3 className="text-[#1C3988] font-bold">{t("bulk_preview")} ({parsedRows.length} {t("bulk_rows")})</h3>
            <span className="text-sm bg-[#E8F5E9] text-[#2E7D32] px-3 py-1 rounded-full font-semibold">
              {validCount} {t("bulk_valid")}
            </span>
            {errorCount > 0 && (
              <span className="text-sm bg-[#FFEBEE] text-[#D32F2F] px-3 py-1 rounded-full font-semibold">
                {errorCount} {t("bulk_with_errors")}
              </span>
            )}
          </div>

          <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-[#E2E8F0] rounded-lg">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F1F5F9]">
                <tr className="text-left text-[#1C3988]">
                  <th className="py-2 px-2">#</th>
                  {columns.map((c) => (
                    <th key={c.key} className="py-2 px-2">{c.label}</th>
                  ))}
                  <th className="py-2 px-2">{t("bulk_status")}</th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row) => (
                  <tr key={row.rowIndex} className={`border-b border-gray-100 ${row.errors.length > 0 ? "bg-red-50" : ""}`}>
                    <td className="py-2 px-2 text-gray-400">{row.rowIndex}</td>
                    {columns.map((c) => (
                      <td key={c.key} className="py-2 px-2">{row.data[c.key] || "-"}</td>
                    ))}
                    <td className="py-2 px-2">
                      {row.errors.length === 0 ? (
                        <span className="text-xs bg-[#E8F5E9] text-[#2E7D32] px-2 py-0.5 rounded-full font-semibold">{t("bulk_ok")}</span>
                      ) : (
                        <span className="text-xs text-[#D32F2F]" title={row.errors.join(", ")}>
                          {row.errors.join("; ")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {validCount > 0 && (
            <button
              className="btn-primary mt-4"
              onClick={uploadData}
              disabled={uploading || !schoolId}
            >
              {uploading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> {t("bulk_uploading")}</>
              ) : (
                <><Upload className="w-5 h-5" /> {t("bulk_upload_btn2")} {validCount} {entityLabel.toLowerCase()}s {t("bulk_to_database")}</>
              )}
            </button>
          )}
          {!schoolId && (
            <p className="text-[#D97706] text-sm mt-3">{t("bulk_select_school_warn")}</p>
          )}
        </div>
      )}

      {results && (
        <div className="mb-5">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-[#E8F5E9] p-4 rounded-lg border border-green-200 text-center">
              <CheckCircle2 className="w-8 h-8 text-[#2E7D32] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#2E7D32]">{results.success}</p>
              <p className="text-sm text-gray-500">{t("bulk_success")}</p>
            </div>
            <div className="bg-[#FFEBEE] p-4 rounded-lg border border-red-200 text-center">
              <AlertCircle className="w-8 h-8 text-[#D32F2F] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#D32F2F]">{results.failed}</p>
              <p className="text-sm text-gray-500">{t("bulk_failed")}</p>
            </div>
          </div>

          {results.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-[200px] overflow-y-auto">
              <h4 className="text-[#D32F2F] font-bold mb-2 text-sm">{t("bulk_errors")}</h4>
              {results.errors.map((err, i) => (
                <p key={i} className="text-sm text-[#D32F2F] mb-1">{err}</p>
              ))}
            </div>
          )}

          <div className="flex gap-3 mt-4">
            <button className="btn-cyan" onClick={reset}>
              <Upload className="w-5 h-5" /> {t("bulk_upload_another")}
            </button>
            <button className="btn-primary" onClick={() => setShowPanel(false)}>
              <CheckCircle2 className="w-5 h-5" /> {t("bulk_done")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
